import type { Sort } from '@/components/table/sort';
import type { PageRequest } from '@/models/listPage';

export interface PeriodSheetFilters {
  /** Chaque mot cherché dans le prénom, le nom ou l'email du collaborateur (côté API : SearchableFilter) */
  search: string;
  /** Id de l'équipe (figée à la soumission) ; null : toutes */
  team: string | null;
  /** Id du type de feuille (Normale, Absence) ; null : tous */
  timeSheetType: string | null;
}

/** Filtres à l'arrivée sur le récapitulatif : aucun */
export const DEFAULT_PERIOD_SHEET_FILTERS: PeriodSheetFilters = { search: '', team: null, timeSheetType: null };

/** Colonnes triables du détail par collaborateur (total : TimeSheetSummary côté API) */
export type PeriodSheetSortKey = 'name' | 'team' | 'total';

export const DEFAULT_PERIOD_SHEET_SORT: Sort<PeriodSheetSortKey> = { key: 'name', direction: 'asc' };

/**
 * Requête GET /time_sheets d'une page du récapitulatif d'une période : les feuilles soumises (statuts donnés, tous sauf
 * Brouillon, comme les « Demandes » de PeriodSummary), d'une équipe, d'un type (Normale, Absence) ou d'un nom cherché, par collaborateur (NOM puis
 * prénom), par équipe ou par total d'heures (puis par collaborateur). scopeTeams : celles du manager, à défaut d'une
 * équipe choisie (null : le Dirigeant, toutes).
 * null tant que les statuts ne sont pas chargés.
 */
export function buildPeriodSheetsRequest(
  periodId: number,
  countedStatusIds: number[],
  { search, team, timeSheetType }: PeriodSheetFilters,
  sort: Sort<PeriodSheetSortKey>,
  page: number,
  itemsPerPage: number,
  scopeTeams: string[] | null = null,
): PageRequest | null {
  if (!countedStatusIds.length) return null;
  const { direction } = sort;
  const byName = { 'member.user.lastName': direction, 'member.user.firstName': direction };

  return {
    filters: {
      period: periodId,
      timeSheetStatus: countedStatusIds,
      search: search.trim() || null,
      'teamMember.team': team ?? scopeTeams,
      timeSheetType,
    },
    // Par équipe ou par total : puis par collaborateur (NOM puis prénom, dans l'ordre alphabétique pour le total)
    order:
      sort.key === 'team'
        ? { 'teamMember.team.name': direction, ...byName }
        : sort.key === 'total'
          ? {
              'timeSheetSummaries.totalMinutes': direction,
              'member.user.lastName': 'asc',
              'member.user.firstName': 'asc',
            }
          : byName,
    page,
    itemsPerPage,
  };
}
